import { Injectable } from '@angular/core';
import { Store } from '@ngrx/store';
import { CalculationLineApi, CalculationTailApi } from 'src/app/types';
import * as CalculationActions from '../../state-management/calculation-actions';
import { v4 } from 'uuid';
import { selectCalculationMeta, selectCalculationState, selectCurrentSelection, selectHourRates, selectLines, selectTail } from 'src/app/state-management/calculation-selectors';
import { firstValueFrom, last } from 'rxjs';
import { showMessage } from 'src/app/state-management/app-actions';
import { isCalculationLineApi, isCalculationTailApi } from 'src/app/state-management/calculation-state';

@Injectable({
    providedIn: 'root'
})
export class CreateLineService {
    constructor(private store: Store) { }

    needsNormalization<T extends { sortOrder: number }>(
        items: T[],
        filterFn?: (item: T) => boolean,
        minAllowedGap = 0.001
    ): boolean {
        // console.debug('[create-line.service] needsNormalization', items, filterFn, minAllowedGap);
        const sorted = filterFn
            ? items.filter(filterFn).sort((a, b) => a.sortOrder - b.sortOrder)
            : [...items].sort((a, b) => a.sortOrder - b.sortOrder);

        let minGap = Infinity;
        for (let i = 1; i < sorted.length; i++) {
            const gap = sorted[i].sortOrder - sorted[i - 1].sortOrder;
            if (gap < minGap) {
                // console.debug('[create-line.service] gap', i, sorted[i].sortOrder, gap, 'minGap', minGap);
                minGap = gap;
            };
        }

        // console.debug('[create-line.service] minGap', minGap, 'minAllowedGap', minAllowedGap);

        // console.debug('[create-line.service] needsNormalization', minGap < minAllowedGap);

        return minGap < minAllowedGap;
    }

    private normalizeSortOrders<T extends { sortOrder: number; id: string }>(
        items: T[]
    ): T[] {
        const sorted = [...items].sort((a, b) => a.sortOrder - b.sortOrder);

        const normalizedAll = sorted
            .map((item, index) => ({
                ...item,
                sortOrder: (index + 1) * 100
            }));

        const normalizedChanged = normalizedAll.filter((item, index) => item.sortOrder !== sorted[index].sortOrder);

        return normalizedChanged;
    }

    async createCalculationLine(
        isTextLine = false,
        belowSelection = true
    ): Promise<void> {
        console.debug('[create-line.service] createLine', isTextLine, belowSelection);
        const [allLines, { selectedLines: [selectedLine] }, rates] = await Promise.all([
            firstValueFrom(this.store.select(selectLines)),
            firstValueFrom(this.store.select(selectCurrentSelection)),
            firstValueFrom(this.store.select(selectHourRates))
        ]);

        console.log('[create-line.service] selectedLine', selectedLine);

        // If there is no selected calculation line, we can't create a new line
        if (!selectedLine || isCalculationTailApi(selectedLine)) {
            // Show message
            this.store.dispatch(showMessage({
                message: 'Selecteer een calculatieregel om een nieuwe regel toe te voegen'
            }));

            return;
        }

        // console.debug('[create-line.service] referenceLine', selectedLine);
        const subcategoryLines = allLines
            .filter(l => l.code === selectedLine.code)
            .sort((a, b) => a.sortOrder - b.sortOrder);

        const referenceIndex = subcategoryLines.findIndex(l => l.id === selectedLine.id);
        const referenceSortOrder = subcategoryLines[referenceIndex]?.sortOrder ?? 0;

        if (!referenceSortOrder) {
            throw new Error('Reference line not found in subcategory lines');
        }

        // Calculate the new sort order based on the reference line
        let newLineOrder: number;
        let newLineIsEndOfSection = false;

        if (belowSelection) {
            if (selectedLine.endOfSection) {
                newLineIsEndOfSection = true;
            }
            // console.debug('[create-line.service] belowSelection', belowSelection, subcategoryLines[referenceIndex]?.sortOrder, subcategoryLines[referenceIndex + 1]?.sortOrder);
            newLineOrder =
                referenceIndex === subcategoryLines.length - 1
                    ? referenceSortOrder + 100
                    : (referenceSortOrder + subcategoryLines[referenceIndex + 1].sortOrder) / 2;
        } else {
            newLineOrder =
                referenceIndex === 0
                    ? referenceSortOrder / 2
                    : (referenceSortOrder + subcategoryLines[referenceIndex - 1].sortOrder) / 2;
        }

        const newLine: CalculationLineApi = {
            code: selectedLine.code,
            id: v4(),
            isTextLine,
            sourceVersion: selectedLine.sourceVersion,
            sortOrder: newLineOrder,
            unit: isTextLine ? undefined : 'stk',
            hours: {},
            material: {},
            equipment: {},
            subcontracting: {},
            endOfSection: newLineIsEndOfSection,
            rate: (rates.find(rate => rate.id === selectedLine.rate) && selectedLine.rate) ?? '00000000-0000-0000-0000-000000000000'
        };

        // ➕ Check op sortOrder dichtheid
        const withNewLine = [...subcategoryLines, newLine];
        if (this.needsNormalization(withNewLine)) {
            console.debug('[create-line.service] Normalizing lines', withNewLine, 'for new line', newLine);
            const normalizedLines = this.normalizeSortOrders(withNewLine);

            if (newLineIsEndOfSection) {
                // If the new line is endOfSection, we need to remove the endOfSection flag from the selected line
                const selectedLineToUpdate = normalizedLines.find(line => line.id === selectedLine.id);
                if (selectedLineToUpdate) {
                    selectedLineToUpdate.endOfSection = false;
                }
            }

            // console.debug('[create-line.service] Normalizing lines', normalizedLines, 'for new line', newLine);
            this.store.dispatch(CalculationActions.bulkUpdateLines({ lines: normalizedLines.filter(line => line.id !== newLine.id) }));

            // Update sort order of the new line
            const newLineSortOrder = normalizedLines.find(line => line.id === newLine.id)?.sortOrder;

            if (newLineSortOrder) {
                newLine.sortOrder = newLineSortOrder;
            }
        } else if (newLineIsEndOfSection) {
            // If the selected line has the endOfSection flag, we need to move that to the new line
            // This check is placed here because the selected line will be updated if normalization is needed
            // Since it will only be possible if the new line is placed below the selected line
            this.store.dispatch(CalculationActions.bulkUpdateLines({ lines: [{ ...selectedLine, endOfSection: false }] }));
        }

        // Also create the new line
        // Need the last value of the meta$ observable to get the current version
        const { projectId, id } = await firstValueFrom(this.store.select(selectCalculationMeta));

        console.debug('[create-line.service] dispatching insertLine', newLine, 'for calculationId', id, 'and projectId', projectId);
        this.store.dispatch(CalculationActions.insertLine({ calculationId: id, projectId, line: newLine }));

    }

    async createTailLine(
        belowSelection = true,
        lineType: string = 'AMOUNT'
    ): Promise<void> {
        console.debug('[create-line.service] createTailLine', belowSelection, lineType);
        const [tailLines, selectedLines] = await Promise.all([
            firstValueFrom(this.store.select(selectTail)),
            firstValueFrom(this.store.select(selectCurrentSelection)),
        ]);

        let referenceIndex = tailLines.length - 1;
        let referenceLine: CalculationTailApi | null = null;

        if (selectedLines !== undefined && selectedLines.selectedLines.length > 0 && isCalculationTailApi(selectedLines.selectedLines[0])) {
            referenceLine = tailLines.find(l => l.id === selectedLines.selectedLines[0].id) ?? null;
            referenceIndex = tailLines.indexOf(referenceLine) ?? -1;
            console.debug('[create-line.service] referenceLine', referenceLine, 'referenceIndex', referenceIndex);
        }

        // console.debug('[create-line.service] referenceLine', selectedLine);
        const sortedTailLines = [...tailLines]
            .sort((a, b) => a.sortOrder - b.sortOrder);

        const referenceSortOrder = sortedTailLines[referenceIndex]?.sortOrder ?? 0;

        let newLineOrder: number;
        let newLineIsEndOfSection = false;

        if (referenceIndex >= 0) {
            if (belowSelection) {
                if (referenceLine?.endOfSection) {
                    console.debug('[create-line.service] referenceLine has endOfSection', referenceLine);
                    newLineIsEndOfSection = true;
                }
                newLineOrder =
                    referenceIndex === tailLines.length - 1
                        ? referenceSortOrder + 100
                        : (referenceSortOrder + tailLines[referenceIndex + 1].sortOrder) / 2;
            } else {
                newLineOrder =
                    referenceIndex === 0
                        ? referenceSortOrder / 2
                        : (referenceSortOrder + tailLines[referenceIndex - 1].sortOrder) / 2;
            }
        } else {
            // Geen reference → voeg toe aan eind
            newLineOrder = tailLines.reduce((max, l) => Math.max(max, l.sortOrder), 0) + 100;
        }

        const newTailLine: CalculationTailApi = {
            id: v4(),
            sourceVersion: referenceLine?.sourceVersion ?? 0,
            sortOrder: newLineOrder,
            type: lineType,
            endOfSection: newLineIsEndOfSection,
        };

        // ➕ Check op sortOrder dichtheid
        const withNewLine = [...sortedTailLines, newTailLine];
        if (this.needsNormalization(withNewLine)) {
            console.debug('[create-line.service] Normalizing lines', withNewLine, 'for new line', newTailLine);

            // Normalize including the new line
            const normalizedLines = this.normalizeSortOrders(withNewLine);

            // If the selected line has the endOfSection flag, we need to remove that from the selected line
            if (newLineIsEndOfSection) {
                // If the new line is endOfSection, we need to remove the endOfSection flag from the selected line
                const selectedLineToUpdate = normalizedLines.find(line => line.id === referenceLine.id);
                if (selectedLineToUpdate) {
                    selectedLineToUpdate.endOfSection = false;
                }
            }

            //  Bulk update lines is done without the new line, because it will not exist yet
            this.store.dispatch(CalculationActions.bulkUpdateTailLines({ lines: normalizedLines.filter(line => line.id !== newTailLine.id) }));

            // Update sort order of the new line
            const newLineSortOrder = normalizedLines.find(line => line.id === newTailLine.id)?.sortOrder;

            // Also remove endOfSection from the selected line if pasted/created below it
            if (newLineSortOrder) {
                newTailLine.sortOrder = newLineSortOrder;
            }
        } else if (newLineIsEndOfSection) {
            // This scenario is
            // 1) No normalization needed
            // 2) Selected line has endOfSection flag

            // In that case, the endOfSection flag is moved to the new line
            // Only need to remove it from the selected line
            // *In case of normalization, the endOfSection flag is removed from the selected line in the normalization step
            this.store.dispatch(CalculationActions.bulkUpdateTailLines({ lines: [{ ...referenceLine, endOfSection: false }] }));
        }

        // Also create the new line
        // Need the last value of the meta$ observable to get the current version
        const { projectId, id } = await firstValueFrom(this.store.select(selectCalculationMeta));

        this.store.dispatch(CalculationActions.insertTailLine({ calculationId: id, projectId, line: newTailLine }));
    }

    async createAndInsertCalculationLines(
        lines: CalculationLineApi[],
        belowReference = true,
        mode: 'paste' | 'create' = 'paste'
    ): Promise<void> {
        console.debug('[create-line.service] createAndInsertTaillLines', lines, belowReference, mode);

        const [allLines, { selectedLines = [] } = {}, rates] = await Promise.all([
            firstValueFrom(this.store.select(selectLines)),
            firstValueFrom(this.store.select(selectCurrentSelection)),
            firstValueFrom(this.store.select(selectHourRates))
        ]);

        const subcategoryLines: CalculationLineApi[] = allLines
            .filter(l =>
                (mode === 'paste' && l.code === (selectedLines[0] as CalculationLineApi)?.code) ||
                (mode === 'create' && l.code === lines[0].code))
            .sort((a, b) => a.sortOrder - b.sortOrder);

        const selectedLine = subcategoryLines.find(l => l.id === selectedLines[0]?.id);
        const lastLine = [...subcategoryLines].pop();
        let newLineIsEndOfSection = false;

        // Difference between paste and create:
        // - paste: lines are pasted below or above the selected line, so we need a selection. Category is copied from the selected line
        // - create: lines are created and category is given for these new lines, so we don't need a selection

        let referenceIndex = 0;

        // So when pasting, we need to check if there is a selected line and if it is a calculation line (not a tail line)
        if (mode === 'paste') {
            if (selectedLine === undefined || !isCalculationLineApi(selectedLine)) {
                // Show message
                this.store.dispatch(showMessage({
                    message: 'Selecteer een calculatieregel om de regels toe te voegen'
                }));

                // Since messages are not shown yet, we also return an error
                this.store.dispatch(CalculationActions.insertLinesFailure({ error: new Error('Geen calculatieregel geselecteerd') }));

                return;
            }

            // Use selected line as reference
            referenceIndex = subcategoryLines.findIndex(l => l.id === selectedLine.id);

            if (belowReference && selectedLine.endOfSection) {
                newLineIsEndOfSection = true;
            }
        } else {
            // Create will always be after the last line
            referenceIndex = subcategoryLines.length - 1;
        }

        // Determine how many positions to move based on insertion below reference
        const offset = belowReference ? 1 : 0;

        // Compute start index for updating, never below zero
        const moveOrderStartIndex = Math.max(0, referenceIndex + offset);

        // First figure out the sort order of the first line. Therefore we need to check the sort order of the line above or below, depening on the insertion
        // If the reference line is the last line, we need to add 100 to the sort order
        const startSortOrder = moveOrderStartIndex >= subcategoryLines.length ? (lastLine?.sortOrder ?? 0) + 100 : subcategoryLines[moveOrderStartIndex]?.sortOrder ?? 100;

        // Create a new array of updated lines with recalculated order
        const updateOrderForLines = subcategoryLines
            .slice(moveOrderStartIndex)
            .map<CalculationLineApi>((line) => ({
                ...line,
                sortOrder: line.sortOrder + lines.length * 100,
            } satisfies CalculationLineApi))

        console.log('[create-line.service] updateOrder', updateOrderForLines, 'startAtOrder', startSortOrder, 'copyLines', lines);

        // Now create the new lines
        const newLines = lines.map((line, i, a) => ({
            code: mode === 'create' ? line.code : (selectedLine as CalculationLineApi).code,
            id: v4(),
            quantity: line.quantity ?? '',
            isTextLine: line.isTextLine,
            description: line.description ?? '',
            unit: line.unit,
            sortOrder: startSortOrder + (i * 100),
            sourceVersion: line.sourceVersion ?? 1,
            hours: { ...line.hours },
            material: { ...line.material },
            equipment: { ...line.equipment },
            subcontracting: { ...line.subcontracting },
            endOfSection: i === a.length - 1 ? line.endOfSection || newLineIsEndOfSection : line.endOfSection,
            rate: (rates.find(rate => rate.id === line.rate) && line.rate) ?? '00000000-0000-0000-0000-000000000000'
        }));

        const { projectId, id } = await firstValueFrom(this.store.select(selectCalculationMeta));

        // No normalization check, because we are always updating the sortOrder after the startAtOrder index
        if (updateOrderForLines.length > 0) {
            // Also remove endOfSection from the selected line if pasted/created below it
            if (newLineIsEndOfSection) {
                // Using bulk here, just because I am lazy. UpdateLine needs calculationId and projectId
                this.store.dispatch(CalculationActions.updateLineUi({ projectId, calculationId: id, line: { ...selectedLine, endOfSection: false } }));
            }

            // Now update the sortOrder of all lines that are moved down
            this.store.dispatch(CalculationActions.bulkUpdateLines({ lines: updateOrderForLines }));
        }

        this.store.dispatch(CalculationActions.insertLines({
            calculationId: id,
            projectId,
            lines: newLines
        }));
    }

    async createAndInsertTailLines(
        lines: CalculationTailApi[],
        belowReference = true,
        mode: 'paste' | 'create' = 'paste'
    ): Promise<void> {
        console.debug('[create-line.service] createAndInsertTaillLines', lines, belowReference, mode);

        const [{ selectedLines = [] } = {}, tail] = await Promise.all([
            firstValueFrom(this.store.select(selectCurrentSelection)),
            firstValueFrom(this.store.select(selectTail)),
        ]);

        // Create copy and sort
        const tailLines = [...tail]
            .sort((a, b) => a.sortOrder - b.sortOrder);

        const selectedLine = selectedLines[0];
        const lastLine = [...tail].pop();
        let newLineIsEndOfSection = false;

        // Difference between paste and create:
        // - paste: lines are pasted below or above the selected line, so we need a selection. Category is copied from the selected line
        // - create: lines are created and category is given for these new lines, so we don't need a selection

        let referenceIndex = 0;

        // So when pasting, we need to check if there is a selected line and if it is a calculation line (not a tail line)
        if (mode === 'paste') {
            if (!selectedLine || !isCalculationTailApi(selectedLine)) {
                // Show message
                this.store.dispatch(showMessage({
                    message: 'Selecteer een staartregel om de regels toe te voegen'
                }));

                // Since messages are not shown yet, we also return an error
                this.store.dispatch(CalculationActions.bulkInsertTailLinesFailure({ error: new Error('Geen staartregel geselecteerd') }));

                return;
            }

            // Use selected line as reference
            referenceIndex = tailLines.findIndex(l => l.id === selectedLine.id);

            if (belowReference && selectedLine?.endOfSection) {
                newLineIsEndOfSection = true;
            }
        } else {
            // Create will always be after the last line
            referenceIndex = tailLines.length - 1;
        }

        // Determine how many positions to move based on insertion below reference
        const offset = belowReference ? 1 : 0;

        // Compute start index for updating, never below zero
        const startIndex = Math.max(0, referenceIndex + offset);

        // First figure out the sort order of the first line. Therefore we need to check the sort order of the line above or below, depening on the insertion
        // If the reference line is the last line, we need to add 100 to the sort order
        const startSortOrder = startIndex >= tailLines.length ? (lastLine?.sortOrder ?? 0) + 100 : tailLines[startIndex]?.sortOrder ?? 100;

        // Create a new array of updated lines with recalculated order
        const updateOrderForLines = tailLines
            .slice(startIndex)
            .map<CalculationTailApi>((line) => ({
                ...line,
                sortOrder: line.sortOrder + lines.length * 100,
            } satisfies CalculationTailApi))

        // Now create the new lines
        const newLines = lines.map((line, i, a) => ({
            ...line,
            id: v4(),
            endOfSection: i === a.length - 1 ? line.endOfSection || newLineIsEndOfSection : line.endOfSection,
            sortOrder: startSortOrder + (i * 100),
        }));

        console.log('[create-line.service] updateOrder', updateOrderForLines, 'startAtOrder', startSortOrder, 'copyLines', newLines);

        // No normalization check, because we are always updating the sortOrder after the startAtOrder index
        if (updateOrderForLines.length > 0) {
            // Also remove endOfSection from the selected line if pasted/created below it
            if (newLineIsEndOfSection) {
                // Using bulk here, just because I am lazy. UpdateLine needs calculationId and projectId
                this.store.dispatch(CalculationActions.bulkUpdateTailLines({ lines: [{ ...selectedLine, endOfSection: false }] }));
            }

            // Now update the sortOrder of all lines that are moved down
            this.store.dispatch(CalculationActions.bulkUpdateTailLines({ lines: updateOrderForLines }));
        }

        this.store.dispatch(CalculationActions.bulkInsertTailLines({
            lines: newLines
        }));
    }
}
